import "@/App.css";
import "@/i18n";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AgentProvider } from "@/context/AgentContext";
import { BillingProvider, useBilling } from "@/context/BillingContext";
import { setUpgradeHandler } from "@/lib/api";
import Protected from "@/components/Protected";
import UpgradeModal from "@/components/UpgradeModal";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import LogrosPage from "@/pages/LogrosPage";
import IAPage from "@/pages/IAPage";
import MetricasPage from "@/pages/MetricasPage";
import BillingPage from "@/pages/BillingPage";

/**
 * AppRouter — detects the Emergent Auth callback synchronously during render
 * (not in useEffect — that runs too late and creates a race with ProtectedRoute).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const { triggerUpgrade } = useBilling();

  // Wire the global 402 interceptor → upgrade modal.
  useEffect(() => {
    setUpgradeHandler(triggerUpgrade);
    return () => setUpgradeHandler(null);
  }, [triggerUpgrade]);

  // Sync i18n with the user's preferred language whenever it changes.
  useEffect(() => {
    if (user?.preferred_language && i18n.language?.split("-")[0] !== user.preferred_language) {
      i18n.changeLanguage(user.preferred_language);
    }
  }, [user?.preferred_language, i18n]);

  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<Protected><LogrosPage /></Protected>} />
      <Route path="/logros" element={<Protected><LogrosPage /></Protected>} />
      <Route path="/ia" element={<Protected><IAPage /></Protected>} />
      <Route path="/metricas" element={<Protected><MetricasPage /></Protected>} />
      <Route path="/billing" element={<Protected><BillingPage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App dark" data-testid="app-root">
      <BrowserRouter>
        <AuthProvider>
          <AgentProvider>
            <BillingProvider>
              <AppRouter />
              <UpgradeModal />
            </BillingProvider>
          </AgentProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
